import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { moneyMinor } from '../../utils/money';
import { useT } from '../../i18n/LanguageContext';
import { fundingReference } from './merchantFundingReceipt';
import {
  matchesAdverseReviewIntent,
  readMerchantAdverseReview,
} from './merchantAdverseReviewReceipt';

/** An admin observation only; fresh provider checks still control credit and release. */
export default function DistributorMerchantAdverseReview({
  distributorId,
  purchase,
  disabled,
  onPendingChange,
  onRefresh,
}) {
  const t = useT();
  const [value, setValue] = useState(null);
  const [reference, setReference] = useState('');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const pending = useRef(false),
    frozen = useRef(null);
  const session = useRef(captureSession()),
    scope = useRef(shippingAccessScope());
  const { begin, version } = useShippingRequest(`${distributorId}:${purchase._id}`);
  useEffect(() => {
    setValue(null);
    setReference('');
    setChecked(false);
    setBusy(false);
    setIntent(null);
    setError('');
    setNotice('');
    pending.current = false;
    frozen.current = null;
  }, [version, distributorId, purchase._id]);
  const current = () =>
    isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  const load = async () => {
    if (!current() || pending.current || disabled) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    setValue(null);
    onPendingChange?.(true);
    const request = begin();
    try {
      const next = readMerchantAdverseReview(
        await distributorService.merchantAdverseReview(distributorId, purchase._id, {
          signal: request.signal,
        }),
        purchase
      );
      if (!request.isCurrent() || !current()) return;
      if (
        frozen.current &&
        next.merchantAdverseReview &&
        !next.canReview &&
        !matchesAdverseReviewIntent(next, frozen.current, session.current.userId)
      )
        setNotice('Another merchant review is already recorded. Review the saved evidence.');
      setValue(next);
      frozen.current = null;
      setIntent(null);
      setReference('');
      setChecked(false);
      onPendingChange?.(false);
      if (next.merchantAdverseReview) onRefresh?.();
    } catch {
      if (request.isCurrent() && current()) {
        setError(
          'Merchant review details are unavailable. Check the saved review before continuing.'
        );
        if (!frozen.current) onPendingChange?.(false);
      }
    } finally {
      if (request.isCurrent() && current()) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (
      !current() ||
      pending.current ||
      disabled ||
      frozen.current ||
      !value?.canReview ||
      !fundingReference(reference) ||
      !checked
    )
      return;
    const requestKey = window.crypto?.randomUUID?.();
    if (!requestKey || !/^[A-Za-z0-9_-]{16,100}$/.test(requestKey)) {
      setError('A secure request key is unavailable. Reload before reviewing.');
      return;
    }
    const captured = {
      requestKey,
      expectedAttemptRevision: value.expectedAttemptRevision,
      realmId: value.realmId,
      transactionId: value.transactionId,
      amountCents: value.amountCents,
      currency: 'USD',
      reviewReference: reference,
      confirmedNoRefundsReturnsOrDisputes: true,
    };
    frozen.current = captured;
    setIntent(captured);
    pending.current = true;
    setBusy(true);
    setError('');
    onPendingChange?.(true);
    const request = begin();
    try {
      const next = readMerchantAdverseReview(
        await distributorService.confirmMerchantAdverseReview(
          distributorId,
          purchase._id,
          captured
        ),
        purchase
      );
      if (!request.isCurrent() || !current()) return;
      if (!matchesAdverseReviewIntent(next, captured, session.current.userId))
        throw new Error('Unconfirmed merchant review');
      setValue(next);
      frozen.current = null;
      setIntent(null);
      setReference('');
      setChecked(false);
      onPendingChange?.(false);
      onRefresh?.();
    } catch {
      if (request.isCurrent() && current())
        setError(
          'Review result is unconfirmed. Check the saved review; nothing will be resent automatically.'
        );
    } finally {
      if (request.isCurrent() && current()) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const change = (setter, next) => {
    if (!current() || pending.current || frozen.current || disabled) return;
    setter(next);
    onPendingChange?.(true);
  };
  return (
    <section
      className="border-t pt-3 space-y-3"
      aria-label={t('Refund, return and dispute review')}
    >
      <button
        type="button"
        disabled={disabled || busy || (Boolean(reference || checked) && !intent)}
        onClick={load}
      >
        {t(intent ? 'Check saved merchant review' : 'Review refunds, returns and disputes')}
      </button>
      {busy && <p role="status">{t('Checking merchant review...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {value && (
        <>
          <p className="break-all">
            {t('Payment transaction')}: {value.transactionId} · {moneyMinor(value.amountCents)} USD
            · {t('QuickBooks company ID')}: {value.realmId}
          </p>
          <p>
            {t(
              'Check this exact payment in QuickBooks Merchant Service Center for refunds, returns and disputes. This records your review only; it does not add units or release orders.'
            )}
          </p>
          {value.merchantAdverseReview && (
            <p role="status">
              {t('No adverse items observed at review')}:{' '}
              {value.merchantAdverseReview.reviewReference} ·{' '}
              {value.merchantAdverseReview.reviewedAt.replace('T', ' ').replace('Z', ' UTC')}
            </p>
          )}
          {value.merchantAdverseReview && value.canReview && (
            <p role="status">
              {t('Review expired. Review the merchant records again before units can be released.')}
            </p>
          )}
          {value.canReview ? (
            <form onSubmit={submit} className="space-y-3">
              <fieldset disabled={disabled || busy || Boolean(intent)} className="space-y-3">
                <label className="block">
                  {t('Merchant review reference')}
                  <input
                    className="block w-full rounded border p-2"
                    maxLength={120}
                    value={reference}
                    onChange={(event) => change(setReference, event.target.value)}
                  />
                </label>
                <p className="text-sm">
                  {t(
                    'Use a reference ID only. Do not enter bank details, credentials or private payment responses.'
                  )}
                </p>
                <label className="block">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => change(setChecked, event.target.checked)}
                  />{' '}
                  {t('I checked this payment for refunds, returns and disputes and found none.')}
                </label>
              </fieldset>
              <button
                type="submit"
                className="me-distributor-admin-primary"
                disabled={
                  disabled || busy || Boolean(intent) || !fundingReference(reference) || !checked
                }
              >
                {t('Record merchant review')}
              </button>
              {!intent && (
                <button type="button" disabled={disabled || busy} onClick={load}>
                  {t('Discard merchant review draft')}
                </button>
              )}
            </form>
          ) : !value.merchantAdverseReview ? (
            <p>{t('Merchant review cannot be recorded for this payment yet.')}</p>
          ) : null}
        </>
      )}
    </section>
  );
}
